import { Autocomplete, TextField, createFilterOptions } from "@mui/material";
import { MRT_Row, MRT_ColumnDef } from "material-react-table";
import { Job, Contact, newSalespersonContact } from "../../../../api";
import { ColumnContext } from "./ColumnHelpers";
import { columnDisplayNames } from "../../../admin/ImportSettings";
import { getValidationErrorKey, isFieldRequired } from "../../../../utilities/jobValidation";
import { CREATE_NEW_SALESPERSON } from "../../../../utilities/constants";

const filter = createFilterOptions<Contact>();

export function column_Salesperson(context: ColumnContext): MRT_ColumnDef<Job, unknown> {
	return {
		id: "salesperson",
		header: columnDisplayNames.salesperson,
		accessorKey: "salespersonIds",
		enableEditing: false,
		size: 300,
		Cell: ({ row }: { row: MRT_Row<Job> }) => {
			const jobId = row.original.id;
			const job = context.jobs.find((j) => j.id === jobId);
			const hasNewSalesperson = job?.salespersonIds?.includes(CREATE_NEW_SALESPERSON);

			const allSalespersons = [...(context.props.salespersons || []), ...context.newSalespersons];

			// For existing salesperson, find the selected one
			const selectedSalesperson = hasNewSalesperson
				? ({ id: CREATE_NEW_SALESPERSON, name: job?.newSalesperson?.name || "New Salesperson" } as Contact)
				: allSalespersons.find((contact) => job?.salespersonIds?.includes(contact.id)) || null;

			const rowContext = context.getRowValidationContext(row);
			const isRequired = isFieldRequired("salesperson", rowContext);

			return (
				<Autocomplete
					size="small"
					freeSolo
					style={{ width: "100%", padding: 0 }}
					value={selectedSalesperson}
					options={allSalespersons}
					isOptionEqualToValue={(option, value) => option.id === value.id}
					getOptionLabel={(option) => {
						if (typeof option === "string") {
							return option.startsWith("New Salesperson") ? option : `New Salesperson: ${option}`;
						}
						return option?.name ?? "";
					}}
					filterOptions={(options, params) => {
						const filtered = filter(options, params);

						if (
							params.inputValue !== "" &&
							!allSalespersons.some((salesperson) => salesperson.name.toLowerCase() === params.inputValue.toLowerCase())
						) {
							filtered.push(newSalespersonContact(CREATE_NEW_SALESPERSON, `New Salesperson: ${params.inputValue}`));
						}

						return filtered;
					}}
					renderInput={(params) => (
						<TextField
							{...params}
							style={{ padding: 0 }}
							error={!!context.validationErrors?.[getValidationErrorKey("salesperson", row.original.id)]}
							helperText={context.validationErrors?.[getValidationErrorKey("salesperson", row.original.id)]}
							required={isRequired}
						/>
					)}
					onChange={(event, value, reason) => {
						// Ignore onChange events that are not from actual user selection
						if (reason === "blur") {
							return;
						}

						const newJobs = [...context.jobs];
						const newJob = newJobs.find((job) => job.id === row.original.id);
						if (!newJob) return;
						const index = newJobs.indexOf(newJob);

						if (typeof value === "string") {
							// Handle free text input
							const newValue = value.startsWith("New Salesperson:") ? value.replace("New Salesperson: ", "") : value;

							if (newValue !== "") {
								// Check if salesperson already exists
								const existingSalesperson = allSalespersons.find(
									(sp) => sp.name.toLowerCase() === newValue.toLowerCase(),
								);

								if (existingSalesperson) {
									// Use existing salesperson
									newJob.salespersonIds = [existingSalesperson.id];
									newJob.newSalesperson = undefined;
								} else {
									// Create new salesperson
									newJob.salespersonIds = [CREATE_NEW_SALESPERSON];
									newJob.newSalesperson = { name: newValue, phone: "" };

									// Add to local salespersons list with a temporary ID
									const tempId = `temp-${Date.now()}-${Math.random()}`;
									const firstName = newValue.split(" ").at(0) || "";
									const lastName = newValue.split(" ").slice(1).join(" ") || "";

									const newSalesperson = newSalespersonContact(tempId, newValue, firstName, lastName);

									// Only add if not already in local list
									if (!context.newSalespersons.some((sp) => sp.name.toLowerCase() === newValue.toLowerCase())) {
										context.setNewSalespersons([...context.newSalespersons, newSalesperson]);
									}
								}
							}
						} else if (value && value.id === CREATE_NEW_SALESPERSON) {
							// Selected "New Salesperson" option
							const name = value.name.replace("New Salesperson: ", "");

							// Check if salesperson already exists
							const existingSalesperson = allSalespersons.find((sp) => sp.name.toLowerCase() === name.toLowerCase());

							if (existingSalesperson) {
								// Use existing salesperson
								newJob.salespersonIds = [existingSalesperson.id];
								newJob.newSalesperson = undefined;
							} else {
								newJob.salespersonIds = [CREATE_NEW_SALESPERSON];
								newJob.newSalesperson = { name, phone: "" };

								// Add to local salespersons list with a temporary ID
								const tempId = `temp-${Date.now()}-${Math.random()}`;
								const firstName = name.split(" ").at(0) || "";
								const lastName = name.split(" ").slice(1).join(" ") || "";

								const newSalesperson = newSalespersonContact(tempId, name, firstName, lastName);

								// Only add if not already in local list
								if (!context.newSalespersons.some((sp) => sp.name.toLowerCase() === name.toLowerCase())) {
									context.setNewSalespersons([...context.newSalespersons, newSalesperson]);
								}
							}
						} else if (value && value.id !== CREATE_NEW_SALESPERSON) {
							// Check if this is a locally-created salesperson
							if (context.isNewSalesperson(value.id)) {
								// This is a locally-created salesperson, treat as new
								const localSp = context.findNewSalesperson(value.id);
								if (localSp) {
									newJob.salespersonIds = [CREATE_NEW_SALESPERSON];
									newJob.newSalesperson = { name: localSp.name, phone: localSp.mobilePhone };
								}
							} else {
								// This is a real existing salesperson from props
								newJob.salespersonIds = [value.id];
								newJob.newSalesperson = undefined;
							}
						} else if (value === null && reason === "clear") {
							// User explicitly cleared the selection
							newJob.salespersonIds = [];
							newJob.newSalesperson = undefined;
						} else {
							// Ignore other cases (like null from blur, etc.)
							return;
						}

						row._valuesCache.salespersonIds = newJob.salespersonIds;
						newJobs[index] = newJob;
						context.setJobs(newJobs);
						context.validateJobs([newJob], { updateState: true, clearPrevious: false });
					}}
				/>
			);
		},
	};
}
